import { IssueItem } from "@/components/issues/issue-item/issue-item";
import { IssueListFrame } from "@/components/issues/issue-list/issue-list-frame";
import { useIssueRows } from "@/components/issues/issue-list/use-issue-rows";
import {
  IssueTableHeader,
  IssueTableRow,
} from "@/components/issues/issue-table-row/issue-table-row";
import {
  IssueStatusNav,
  type StatusSelection,
} from "@/components/issues/issue-status-nav/issue-status-nav";
import { issueStatusLabel } from "@/components/issues/issue-status-pill/issue-status-pill";
import { useIssueStatusAccess } from "@/hooks/use-issue-status-access";
import type {
  IssueStatus,
  IssueSummary,
  ProjectIssue,
  ReportCollectionItem,
} from "@/types/api";
import { ItemCell, Tombstone } from "./collection-item-cell";

interface ReportIssueListProps {
  projectId: string;
  items: ReportCollectionItem[];
  /**
   * Every page of the report is loaded. The tabs show counts only then: a count
   * that grows as pages land is a number someone may act on.
   */
  complete: boolean;
  editing: boolean;
  onRemove: (itemId: string) => void;
  status: StatusSelection;
  onStatusChange: (status: StatusSelection) => void;
  onOpenIssue?: (issue: ProjectIssue) => void;
  /** The issue open in the side panel, highlighted among the cards. */
  activeIssueId: string | null;
  /** The side panel is open: cards, since six columns no longer fit. */
  forceCards: boolean;
}

const STATUSES: IssueStatus[] = ["assigned", "in_progress", "completed", "closed"];

/**
 * An issues report's contents: the status tabs, then the issues drawn the way
 * the Issues page draws them — table or cards, the status shortcut on each row,
 * in the same bordered container. The set is the report's, in the report's own
 * order, so the table's titles are plain labels. See [[report-detail-page.spec]]
 * and [[issue-list-frame.spec]].
 */
export function ReportIssueList({
  projectId,
  items,
  complete,
  editing,
  onRemove,
  status,
  onStatusChange,
  onOpenIssue,
  activeIssueId,
  forceCards,
}: ReportIssueListProps) {
  const statusAccessFor = useIssueStatusAccess(projectId);
  const { asTable, updateStatusVia, updateHandlerFor, statusSheet } =
    useIssueRows({ projectId, statusAccessFor, forceCards });

  // Removed issues have no status, so they show under All and nowhere else.
  const live = items.filter((item) => item.issue && !item.is_deleted);
  const summary: IssueSummary | undefined = complete
    ? STATUSES.reduce(
        (counts, s) => ({
          ...counts,
          [s]: live.filter((item) => item.issue?.status === s).length,
        }),
        {} as IssueSummary,
      )
    : undefined;
  const visible = status
    ? live.filter((item) => item.issue?.status === status)
    : items;

  return (
    <>
      <IssueStatusNav
        value={status}
        onChange={onStatusChange}
        summary={summary}
        totalCount={complete ? live.length : undefined}
        className="px-0"
      />

      {visible.length === 0 && status ? (
        // Not "this report is empty": the report holds issues, none of them
        // in this status. Said only once the whole report is loaded — until
        // then the loading row (or the retry line) under the list speaks, since
        // a later page may still hold some.
        complete && (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No {issueStatusLabel(status).toLowerCase()} issues in this report.
          </p>
        )
      ) : (
        <IssueListFrame>
          {asTable && <IssueTableHeader />}
          {visible.map((item) => (
            <ItemCell
              key={item.id}
              item={item}
              editing={editing}
              onRemove={() => onRemove(item.id)}
              compact={asTable}
            >
              {item.issue && !item.is_deleted ? (
                asTable ? (
                  <IssueTableRow
                    projectId={projectId}
                    issue={item.issue}
                    onOpen={onOpenIssue}
                    onUpdateStatus={updateHandlerFor(item.issue)}
                  />
                ) : (
                  <IssueItem
                    projectId={projectId}
                    issue={item.issue}
                    onOpen={onOpenIssue}
                    isActive={item.issue.id === activeIssueId}
                    onUpdateStatus={updateHandlerFor(item.issue)}
                    updateStatusVia={updateStatusVia}
                  />
                )
              ) : (
                <Tombstone inline />
              )}
            </ItemCell>
          ))}
        </IssueListFrame>
      )}
      {statusSheet}
    </>
  );
}
